'use client';

import React, { useEffect, useRef } from 'react';
import AppImage from '@/components/ui/AppImage';

export default function HeroSection() {
  const headlineRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = headlineRef?.current;
    if (!el) return;
    const handleScroll = () => {
      const y = window.scrollY;
      el.style.transform = `translateY(${y * 0.18}px)`;
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <section className="relative w-full h-screen min-h-[600px] overflow-hidden">
      {/* Full-viewport photograph */}
      <div className="absolute inset-0 z-0">
        <AppImage
          src="/assets/images/_DSC4148-1772961452637.jpg"
          alt="Matte black BMW M3 shot front-on with a lake and dramatic overcast sky in the background"
          fill
          className="object-cover object-center w-full h-full"
          priority
        />
        {/* Multi-layer atmospheric overlay */}
        <div className="absolute inset-0 bg-ink/60" />
        <div className="absolute inset-0 bg-gradient-to-r from-ink/85 via-ink/35 to-transparent" />
        {/* Grain overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-ink/95 via-transparent to-ink/30" />
      </div>

      {/* Centered welcome text overlay */}
      <div className="absolute inset-0 z-10 flex flex-col items-center justify-center pointer-events-none px-4 sm:px-8">
        <h2
          className="font-serif text-parchment text-center leading-tight tracking-[-0.01em] mb-2 sm:mb-4 w-full"
          style={{ fontSize: 'clamp(1.4rem, 5vw, 5rem)', fontWeight: 700 }}
        >
          Welcome to Knxckout Media
        </h2>
        <p
          className="font-serif italic text-parchment/75 text-center font-light tracking-wide w-full"
          style={{ fontSize: 'clamp(0.85rem, 2.2vw, 1.75rem)' }}
        >
          Where Crafting The Perfect Moment, Becomes Reality
        </p>
        <div className="flex items-center justify-center gap-2 sm:gap-3 mt-2 sm:mt-3 w-full">
          <div className="h-px w-6 sm:w-10 bg-purple-500/60 flex-shrink-0"></div>
          <p
            className="text-parchment/50 text-center font-light tracking-widest uppercase flex-shrink-0"
            style={{ fontSize: 'clamp(0.55rem, 1.1vw, 0.85rem)' }}
          >
            Based in the Pacific Northwest
          </p>
          <div className="h-px w-6 sm:w-10 bg-purple-500/60 flex-shrink-0"></div>
        </div>
      </div>

      {/* Manifesto headline */}
      <div ref={headlineRef} className="absolute inset-0 z-10 flex items-end">
        <div className="max-w-8xl mx-auto w-full px-4 sm:px-8 md:px-14 pb-16 sm:pb-20 md:pb-28">
          <div className="max-w-full sm:max-w-[75%] md:max-w-[65%] lg:max-w-[58%]">
            {/* Eyebrow */}
            <p
              className="text-gold text-[0.6rem] sm:text-[0.65rem] tracking-[0.22em] sm:tracking-[0.28em] uppercase font-medium mb-6 sm:mb-8 opacity-0 reveal-hidden reveal-delay-1"
              style={{ animationFillMode: 'forwards' }}
            >
              Car Culture &middot; Event Photography &middot; Motion
            </p>

            {/* Manifesto headline */}
            <h1
              className="font-serif text-parchment leading-[0.9] tracking-[-0.02em] mb-8 sm:mb-10 reveal-hidden"
              style={{
                fontSize: 'clamp(2.4rem, 8vw, 7rem)',
                animationFillMode: 'forwards',
              }}
            >
              Every scene
              <br />
              <span className="italic font-light text-parchment/75">has a frame.</span>
              <br />
              <em className="not-italic" style={{ color: 'var(--color-parchment)' }}>
                I find it.
              </em>
            </h1>

            {/* Thin rule */}
            <div className="w-12 sm:w-16 h-px bg-gold mb-6 sm:mb-8 reveal-hidden reveal-delay-2" />

            {/* Sub-manifesto */}
            <p className="text-parchment/50 text-sm md:text-base font-light leading-relaxed max-w-xs sm:max-w-sm tracking-wide reveal-hidden reveal-delay-3">
              Candid. Editorial. Present in the scene.
              <br />
              Capturing automotive culture as it happens.
            </p>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-start gap-4 sm:gap-5 mt-8 sm:mt-10 reveal-hidden reveal-delay-3">
              <a href="#portfolio" className="gold-btn text-center">
                <span>View the Work</span>
              </a>
              <a
                href="https://ig.me/m/knxckout.media"
                target="_blank"
                rel="noopener noreferrer"
                className="ghost-btn text-center"
              >
                <span>Book a Shoot</span>
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll signal — hidden on mobile */}
      <div className="hidden sm:flex absolute bottom-8 right-8 md:right-14 z-10 flex-col items-center gap-3">
        <span className="text-parchment/25 text-[0.6rem] tracking-[0.2em] uppercase rotate-90 origin-center translate-x-3">
          Scroll
        </span>
        <div className="w-px h-14 bg-gradient-to-b from-parchment/30 to-transparent" />
      </div>
    </section>
  );
}